import axios from 'axios';

// Determine API base URL based on environment
const getApiBaseUrl = () => {
  // In production, use the same domain as the frontend
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  
  // In development, use environment variable or default to localhost
  return process.env.REACT_APP_API_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses by redirecting to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login/register pages
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication Types
export interface User {
  id: number;
  email: string;
  gender: string;
  is_vip: boolean;
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  gender: string;
}

export interface GoogleAuthRequest {
  credential: string;
}

export interface Token {
  access_token: string;
  token_type: string;
}

export interface UserProfileUpdate {
  gender: string;
}

// Admin Types
export interface AdminUserUpdate {
  is_vip: boolean;
}

export interface AdminCreditUpdate {
  credits_to_add: number;
  operation_type?: string;
}

export interface AdminCreditReset {
  reset_to_tier_default?: boolean;
}

export interface AdminUnlimitedToggle {
  is_unlimited: boolean;
}

export interface AdminUserResponse {
  id: number;
  email: string;
  gender: string;
  is_vip: boolean;
  is_unlimited: boolean;
  subscription_tier: string;
  subscription_status: string;
  credits_remaining: number;
  monthly_credits_limit: number;
  credits_used: number;
  next_reset: string | null;
  created_at: string;
  updated_at: string;
  total_courses: number;
  total_sessions: number;
  total_practice_sessions: number;
}

// Types
export interface Course {
  id: number;
  name: string;
  description?: string;
  is_archived: boolean;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export interface StudySession {
  id: number;
  title: string;
  is_shared?: boolean;
  share_token?: string | null;
  description?: string;
  ai_generated_description?: string;
  learning_focus?: string;
  course_id: number;
  course?: Course;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  completion_percentage: number;
  documents: Document[];
  transcriptions: Transcription[];
  study_materials: StudyMaterial[];
  questions: Question[];
}

export interface Document {
  id: number;
  filename: string;
  original_filename: string;
  file_type: string;
  upload_date: string;
}

export interface Transcription {
  id: number;
  filename: string;
  original_filename: string;
  status: string;
  language: string;
  created_at: string;
  completed_at?: string;
  transcribed_text?: string;
  transcript_segments?: string;
}

export interface StudyMaterial {
  id: number;
  material_type: string;
  title: string;
  content: string;
  created_at: string;
}

export interface QuestionOption {
  id: number;
  option_text: string;
  is_correct: boolean;
}

export interface Question {
  id: number;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  explanation?: string;
  difficulty_level: 'easy' | 'medium' | 'hard';
  created_at: string;
  options: QuestionOption[];
}

export interface CreateCourseRequest {
  name: string;
  description?: string;
}

export interface CreateSessionRequest {
  title: string;
  description?: string;
  course_id: number;
  learning_focus?: string;
}

export interface SessionCompleteRequest {
  completion_percentage?: number;
}

export interface CourseProgressResponse {
  course_id: number;
  total_sessions: number;
  completed_sessions: number;
  progress_percentage: number;
  sessions_with_status: Array<{
    id: number;
    title: string;
    status: string;
    completion_percentage: number;
    completed_at?: string;
  }>;
}

export interface GenerateMaterialsRequest {
  session_id: number;
  material_types: string[];
}

export interface GeneratePodcastRequest {
  session_id: number;
  voice_id: string;
}

export interface PodcastResponse {
  voice_id: string;
  voice_label?: string;
  script: string;
  audio_mime_type?: string;
  audio_url?: string;
  s3_uri?: string;
  duration_seconds?: number;
  generated_at?: string;
}

export interface GenerateQuestionsRequest {
  session_id: number;
  question_types: string[];
  num_questions: number;
  previous_question_texts?: string[];  // To avoid duplicates when regenerating
  difficulty_preference?: string;      // "easier", "same", "harder"
}

export interface AnswerQuestionRequest {
  question_id: number;
  selected_option_id?: number;
  answer_text?: string;
}

export interface QuestionAnswerResponse {
  is_correct?: boolean;
  selected_option?: string;
  correct_option?: string;
  user_answer?: string;
  explanation: string;
}

export type ChatMode = 'fast' | 'smart';

export interface ChatRequest {
  session_id: number;
  message: string;
  mode?: ChatMode;
}

export interface GraphEvalRequest {
  input_text: string;
}

export interface GraphEvalEntry {
  id: string;
  input_text: string;
  generated_content: string;
  model: string;
  created_at: string;
  created_by?: number;
}

export interface GraphEvalListResponse {
  entries: GraphEvalEntry[];
}

// Practice Session Types
export interface PracticeModeInfo {
  mode: string;
  name: string;
  description: string;
  estimated_time_minutes: number;
  question_count: number;
  is_available: boolean;
  requirements?: string;
}

export interface CoursePracticeModesResponse {
  course_id: number;
  total_questions: number;
  completed_sessions: number;
  total_sessions: number;
  available_modes: PracticeModeInfo[];
}

export interface StartPracticeRequest {
  practice_mode: string;
  session_filter?: number[];
  num_questions?: number;
}

export interface PracticeSession {
  id: number;
  course_id: number;
  practice_mode: string;
  total_questions: number;
  current_question_index: number;
  correct_answers: number;
  started_at: string;
  completed_at?: string;
  time_spent_seconds: number;
}

export interface PracticeQuestion {
  question: Question;
  session_title: string;
  question_number: number;
  total_questions: number;
}

export interface PracticeAnswerRequest {
  practice_session_id: number;
  question_id: number;
  selected_option_id?: number;
  answer_text?: string;
}

export interface PracticeAnswerResponse {
  is_correct: boolean;
  practice_session_completed: boolean;
  current_question_index: number;
  correct_answers: number;
}

export interface WrongAnswerDetail {
  question: Question;
  selected_option?: QuestionOption;
  correct_option: QuestionOption;
  session_title: string;
}

export interface PracticeResults {
  practice_session: PracticeSession;
  score_percentage: number;
  correct_answers: number;
  total_questions: number;
  time_spent_minutes: number;
  weak_sessions: string[];
  improvement_suggestions: string[];
  wrong_answers: WrongAnswerDetail[];
}

// API Functions
export const coursesApi = {
  // Courses
  createCourse: (data: CreateCourseRequest): Promise<Course> =>
    api.post('/api/courses/', data).then(res => res.data),
  
  getAllCourses: (): Promise<Course[]> =>
    api.get('/api/courses/').then(res => res.data),
  
  getCourse: (id: number): Promise<Course> =>
    api.get(`/api/courses/${id}`).then(res => res.data),
  
  updateCourse: (id: number, data: Partial<CreateCourseRequest>): Promise<Course> =>
    api.put(`/api/courses/${id}`, data).then(res => res.data),
  
  deleteCourse: (id: number): Promise<void> =>
    api.delete(`/api/courses/${id}`).then(res => res.data),
  
  getCourseSessions: (id: number): Promise<StudySession[]> =>
    api.get(`/api/courses/${id}/sessions`).then(res => res.data),
  
  getCourseProgress: (id: number): Promise<CourseProgressResponse> =>
    api.get(`/api/courses/${id}/progress`).then(res => res.data),
  
  getArchivedCourses: (): Promise<Course[]> =>
    api.get('/api/courses/archived').then(res => res.data),
  
  archiveCourse: (id: number): Promise<{ message: string }> =>
    api.post(`/api/courses/${id}/archive`).then(res => res.data),
  
  unarchiveCourse: (id: number): Promise<{ message: string }> =>
    api.post(`/api/courses/${id}/unarchive`).then(res => res.data),
  
  // Practice API
  getPracticeModes: (id: number): Promise<CoursePracticeModesResponse> =>
    api.get(`/api/courses/${id}/practice/modes`).then(res => res.data),
  
  startPracticeSession: (id: number, data: StartPracticeRequest): Promise<PracticeSession> =>
    api.post(`/api/courses/${id}/practice/start`, data).then(res => res.data),
};

export const studySessionsApi = {
  // Study Sessions
  createSession: (data: CreateSessionRequest): Promise<StudySession> =>
    api.post('/api/study/sessions', data).then(res => res.data),
  
  getAllSessions: (): Promise<StudySession[]> =>
    api.get('/api/study/sessions').then(res => res.data),
  
  getSession: (id: number): Promise<StudySession> =>
    api.get(`/api/study/sessions/${id}`).then(res => res.data),
  
  updateSession: (id: number, data: Partial<CreateSessionRequest>): Promise<StudySession> =>
    api.put(`/api/study/sessions/${id}`, data).then(res => res.data),
  
  deleteSession: (id: number): Promise<void> =>
    api.delete(`/api/study/sessions/${id}`).then(res => res.data),
  
  getSessionSummary: (id: number): Promise<any> =>
    api.get(`/api/study/sessions/${id}/summary`).then(res => res.data),
  
  completeSession: (id: number, data: SessionCompleteRequest): Promise<StudySession> =>
    api.post(`/api/study/sessions/${id}/complete`, data).then(res => res.data),
};

export const transcriptionApi = {
  uploadAudio: (sessionId: number, file: File, onUploadProgress?: (progressEvent: any) => void): Promise<Transcription> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/transcription/upload?session_id=${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      // Ensure request isn't buffered
      maxBodyLength: Infinity,
      maxContentLength: Infinity
    }).then(res => res.data);
  },
  
  getTranscriptionStatus: (id: number): Promise<Transcription> =>
    api.get(`/api/transcription/status/${id}`).then(res => res.data),
  
  getSessionTranscriptions: (sessionId: number): Promise<Transcription[]> =>
    api.get(`/api/transcription/session/${sessionId}`).then(res => res.data),
  
  getTranscriptionText: (id: number): Promise<{ transcribed_text: string }> =>
    api.get(`/api/transcription/text/${id}`).then(res => res.data),
  
  deleteTranscription: (id: number): Promise<{ message: string }> =>
    api.delete(`/api/transcription/${id}`).then(res => res.data),
  
  cancelTranscription: (id: number): Promise<{ message: string }> =>
    api.post(`/api/transcription/${id}/cancel`).then(res => res.data),
};

export const documentsApi = {
  uploadDocument: (sessionId: number, file: File, onUploadProgress?: (progressEvent: any) => void): Promise<Document> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/documents/upload?session_id=${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      // Ensure request isn't buffered
      maxBodyLength: Infinity,
      maxContentLength: Infinity
    }).then(res => res.data);
  },
  
  getSessionDocuments: (sessionId: number): Promise<Document[]> =>
    api.get(`/api/documents/session/${sessionId}`).then(res => res.data),
  
  getDocumentText: (id: number): Promise<{ extracted_text: string }> =>
    api.get(`/api/documents/text/${id}`).then(res => res.data),
  
  deleteDocument: (id: number): Promise<void> =>
    api.delete(`/api/documents/${id}`).then(res => res.data),
};

export const aiApi = {
  generateMaterials: (data: GenerateMaterialsRequest): Promise<{ generated_materials: StudyMaterial[] }> =>
    api.post('/api/ai/generate-materials', data).then(res => res.data),
  generatePodcast: (data: GeneratePodcastRequest): Promise<PodcastResponse> =>
    api.post('/api/ai/generate-podcast', data).then(res => res.data),
  getPodcast: (sessionId: number): Promise<PodcastResponse> =>
    api.get(`/api/ai/study-sessions/${sessionId}/podcast`).then(res => res.data),

  chatWithMaterials: (data: ChatRequest): Promise<{ response: string; mode?: ChatMode }> =>
    api.post('/api/ai/chat', data).then(res => res.data),

  clearChatSession: (data: ChatRequest): Promise<{ status: string; message: string }> =>
    api.post('/api/ai/chat/clear-session', data).then(res => res.data),
  
  getChatHistory: (sessionId: number, mode?: ChatMode): Promise<{history: Array<{type: 'user' | 'ai', message: string}>, session_id: number, mode?: ChatMode}> =>
    api.get(`/api/ai/sessions/${sessionId}/chat/history`, {
      params: mode ? { mode } : undefined
    }).then(res => res.data),
  
  getCourseChatHistory: (courseId: number, mode?: ChatMode): Promise<{history: Array<{type: 'user' | 'ai', message: string}>, course_id: number, mode?: ChatMode}> =>
    api.get(`/api/ai/course/${courseId}/chat/history`, {
      params: mode ? { mode } : undefined
    }).then(res => res.data),
  
  chatWithCourseMaterials: (courseId: number, message: string, mode: ChatMode): Promise<{ response: string; mode?: ChatMode }> =>
    api.post(`/api/ai/course/${courseId}/chat`, { course_id: courseId, message, mode }).then(res => res.data),
  
  getSessionMaterials: (sessionId: number): Promise<StudyMaterial[]> =>
    api.get(`/api/ai/session/${sessionId}/materials`).then(res => res.data),
  
  getMaterialContent: (id: number): Promise<StudyMaterial> =>
    api.get(`/api/ai/material/${id}`).then(res => res.data),

  generateGraphEval: (data: GraphEvalRequest): Promise<GraphEvalEntry> =>
    api.post('/api/ai/graph-evals', data).then(res => res.data),

  getGraphEvalEntries: (limit: number = 20): Promise<GraphEvalListResponse> =>
    api.get('/api/ai/graph-evals', { params: { limit } }).then(res => res.data),
};

// Feedback API
export const feedbackApi = {
  chatWithFeedback: (message: string, sessionKey?: string): Promise<{ response: string; completed: boolean; session_key: string }> =>
    api.post('/api/ai/feedback/chat', { message, session_key: sessionKey }).then(res => res.data),
};

export const questionsApi = {
  generateQuestions: (data: GenerateQuestionsRequest): Promise<any> =>
    api.post(`/api/questions/sessions/${data.session_id}/questions/generate`, data).then(res => res.data),
  
  getSessionQuestions: (sessionId: number): Promise<Question[]> =>
    api.get(`/api/questions/sessions/${sessionId}/questions`).then(res => res.data),
  
  deleteSessionQuestions: (sessionId: number): Promise<void> =>
    api.delete(`/api/questions/sessions/${sessionId}/questions`).then(res => res.data),
  
  answerQuestion: (data: AnswerQuestionRequest): Promise<QuestionAnswerResponse> =>
    api.post(`/api/questions/questions/${data.question_id}/answer`, data).then(res => res.data),
};

export const practiceApi = {
  getCurrentQuestion: (practiceSessionId: number): Promise<PracticeQuestion> =>
    api.get(`/api/courses/practice-sessions/${practiceSessionId}/question`).then(res => res.data),
  
  submitAnswer: (practiceSessionId: number, data: PracticeAnswerRequest): Promise<PracticeAnswerResponse> =>
    api.post(`/api/courses/practice-sessions/${practiceSessionId}/answer`, data).then(res => res.data),
  
  getResults: (practiceSessionId: number): Promise<PracticeResults> =>
    api.get(`/api/courses/practice-sessions/${practiceSessionId}/results`).then(res => res.data),
};

// Forgot Password types
interface ForgotPasswordRequest {
  email: string;
}

interface ResetPasswordRequest {
  token: string;
  new_password: string;
}

interface VerifyEmailRequest {
  token: string;
}

interface ResendVerificationRequest {
  email: string;
}

interface MessageResponse {
  message: string;
}

interface RegisterResponse {
  message: string;
  email: string;
}

// Authentication API
export const authApi = {
  login: (data: LoginRequest): Promise<Token> =>
    api.post('/api/auth/login', data).then(res => res.data),

  loginWithGoogle: (data: GoogleAuthRequest): Promise<Token> =>
    api.post('/api/auth/google', data).then(res => res.data),
  
  register: (data: RegisterRequest): Promise<RegisterResponse> =>
    api.post('/api/auth/register', data).then(res => res.data),
  
  getCurrentUser: (): Promise<User> =>
    api.get('/api/auth/me').then(res => res.data),
  
  updateProfile: (data: UserProfileUpdate): Promise<User> =>
    api.put('/api/auth/me', data).then(res => res.data),
  
  logout: (): Promise<void> =>
    api.post('/api/auth/logout').then(res => res.data),
  
  forgotPassword: (data: ForgotPasswordRequest): Promise<MessageResponse> =>
    api.post('/api/auth/forgot-password', data).then(res => res.data),
  
  resetPassword: (data: ResetPasswordRequest): Promise<MessageResponse> =>
    api.post('/api/auth/reset-password', data).then(res => res.data),
  
  verifyEmail: (data: VerifyEmailRequest): Promise<MessageResponse> =>
    api.post('/api/auth/verify-email', data).then(res => res.data),
  
  resendVerification: (data: ResendVerificationRequest): Promise<MessageResponse> =>
    api.post('/api/auth/resend-verification', data).then(res => res.data),
};

// Usage Tracking Types
export interface UsageEvent {
  id: number;
  event_type: string;
  category: string;
  estimated_cost_usd: number;
  actual_cost_usd: number | null;
  input_tokens: number;
  output_tokens: number;
  duration_seconds: number;
  file_size_bytes: number;
  model_used: string | null;
  provider_used: string | null;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  user_email: string | null;
}

export interface UsageEventsResponse {
  events: UsageEvent[];
  total_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface AdminUsageStats {
  total_users: number;
  active_users_30d: number;
  total_cost_30d: number;
  transcription_hours_30d: number;
  ai_tokens_30d: number;
  cost_by_category: { [key: string]: number };
  cost_by_provider: { [key: string]: number };
  top_users: Array<{
    user_id: number;
    email: string;
    total_cost: number;
    is_vip: boolean;
  }>;
  daily_costs: Array<{
    date: string;
    cost: number;
  }>;
  daily_usage: Array<{
    date: string;
    count: number;
  }>;
}

export interface UserUsageStats {
  current_period_start: string;
  current_period_end: string;
  total_cost_usd: number;
  transcription_hours: number;
  ai_input_tokens: number;
  ai_output_tokens: number;
  cost_by_category: { [key: string]: number };
  transcriptions_count: number;
  study_sessions_count: number;
  exams_processed_count: number;
  chat_messages_count: number;
  recent_events: UsageEvent[];
}

// Credit Analytics Types
export interface CreditAnalytics {
  period_days: number;
  start_date: string;
  end_date: string;
  total_transactions: number;
  total_credits_used: number;
  total_credits_purchased: number;
  revenue_estimate_ils: number;
  tier_distribution: { [tier: string]: number };
  usage_by_action: { 
    [action: string]: {
      total_credits: number;
      transaction_count: number;
    }
  };
  top_consumers: Array<{
    email: string;
    tier: string;
    credits_used: number;
  }>;
  daily_usage_trend: Array<{
    date: string;
    credits_used: number;
    credits_added: number;
  }>;
  average_usage_by_tier: { [tier: string]: number };
}

export interface CreditTransaction {
  id: number;
  user_id: number;
  user_email: string;
  action_type: string;
  credits_used: number;
  credits_added: number;
  credits_before: number;
  credits_after: number;
  amount_ils: number | null;
  stripe_session_id: string | null;
  description: string | null;
  created_at: string;
}

export interface CreditTransactionsResponse {
  transactions: CreditTransaction[];
  total_count: number;
  limit: number;
  offset: number;
}

export interface SubscriptionAnalytics {
  subscription_distribution: { 
    [tier: string]: { 
      [status: string]: number 
    } 
  };
  active_subscriptions: number;
  tier_totals: { [tier: string]: number };
  unlimited_users: number;
  total_users: number;
}

// Admin API - only accessible to admin users
export const adminApi = {
  getAllUsers: (): Promise<AdminUserResponse[]> =>
    api.get('/api/admin/users').then(res => res.data),
  
  updateUser: (userId: number, data: AdminUserUpdate): Promise<AdminUserResponse> =>
    api.put(`/api/admin/users/${userId}`, data).then(res => res.data),
  
  getSystemStats: (): Promise<{
    total_users: number;
    total_courses: number;
    total_sessions: number;
    total_practice_sessions: number;
    vip_users: number;
  }> =>
    api.get('/api/admin/stats').then(res => res.data),
  
  // Usage Analytics
  getUsageStats: (days: number = 30): Promise<AdminUsageStats> =>
    api.get(`/api/admin/usage/stats?days=${days}`).then(res => res.data),
  
  getUsageEvents: (options: {
    user_id?: number;
    event_type?: string;
    days?: number;
    limit?: number;
    offset?: number;
  } = {}): Promise<UsageEventsResponse> => {
    const params = new URLSearchParams();
    if (options.user_id) params.set('user_id', options.user_id.toString());
    if (options.event_type) params.set('event_type', options.event_type);
    if (options.days) params.set('days', options.days.toString());
    if (options.limit) params.set('limit', options.limit.toString());
    if (options.offset) params.set('offset', options.offset.toString());
    
    return api.get(`/api/admin/usage/events?${params.toString()}`).then(res => res.data);
  },
  
  getUserUsageStats: (userId: number, days: number = 30): Promise<UserUsageStats> =>
    api.get(`/api/admin/users/${userId}/usage?days=${days}`).then(res => res.data),

  // Credit Analytics
  getCreditAnalytics: (days: number = 30): Promise<CreditAnalytics> =>
    api.get(`/api/admin/credit-analytics?days=${days}`).then(res => res.data),

  getCreditTransactions: (options: {
    limit?: number;
    offset?: number;
    user_id?: number;
    action_type?: string;
  } = {}): Promise<CreditTransactionsResponse> => {
    const params = new URLSearchParams();
    if (options.limit) params.set('limit', options.limit.toString());
    if (options.offset) params.set('offset', options.offset.toString());
    if (options.user_id) params.set('user_id', options.user_id.toString());
    if (options.action_type) params.set('action_type', options.action_type);
    
    return api.get(`/api/admin/credit-transactions?${params.toString()}`).then(res => res.data);
  },

  getSubscriptionAnalytics: (): Promise<SubscriptionAnalytics> =>
    api.get('/api/admin/subscription-analytics').then(res => res.data),

  // Credit Management
  grantCredits: (userId: number, data: AdminCreditUpdate): Promise<AdminUserResponse> =>
    api.post(`/api/admin/users/${userId}/credits/grant`, data).then(res => res.data),

  resetCredits: (userId: number, data: AdminCreditReset = { reset_to_tier_default: true }): Promise<AdminUserResponse> =>
    api.post(`/api/admin/users/${userId}/credits/reset`, data).then(res => res.data),

  toggleUnlimited: (userId: number, data: AdminUnlimitedToggle): Promise<AdminUserResponse> =>
    api.post(`/api/admin/users/${userId}/unlimited/toggle`, data).then(res => res.data),
};

// User Usage API
export const usageApi = {
  getMyUsageStats: (days: number = 30): Promise<UserUsageStats> =>
    api.get(`/api/usage/my-usage?days=${days}`).then(res => res.data),
};

// Auth utilities
export const setAuthToken = (token: string) => {
  localStorage.setItem('token', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('token');
};

export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};

// Stripe API
export const stripeApi = {
  createPortalSession: (): Promise<{ url: string }> =>
    api.post('/api/stripe/create-portal-session').then(res => res.data),
  
  createSubscriptionCheckout: (plan_id: string): Promise<{ session_id: string; url: string }> =>
    api.post('/api/stripe/create-subscription-checkout', { plan_id }).then(res => res.data),
  
  getSubscriptionPlans: (language: string = 'he'): Promise<any> =>
    api.get(`/api/stripe/subscription-plans?language=${language}`).then(res => res.data),
  
  getUserPaymentStatus: (): Promise<any> =>
    api.get('/api/stripe/user-payment-status').then(res => res.data),
};

export default api; 
